// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import { formatDisplayDate } from '../../utils/date.js';

// MUI Imports
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';

function SpecialInputField(props) {
    const { offers, title, label, icon, inputType, emptyStateText, path, options, optionsDescription, modal, setModal, setOffers, disabled, value, setValue, forceNoPadding } = props;
    const theme = useTheme();

    const buttonText = (() => {
        if (inputType === 'validity') {
            if (!value.validUntil && !value.validFrom) {
                return emptyStateText;
            }

            if (value.validUntil && value.validFrom) {
                return `${value.validUntil} - ${value.validFrom}`;
            }

            if (value.validUntil && !value.validFrom) {
                return `Until ${value.validUntil}`;
            }

            if (!value.validUntil && value.validFrom) {
                return `From ${value.validFrom}`;
            }
        }

        // A quote's or section's validity: both dates live on the object at `path`.
        if (inputType === 'validity-range') {
            const { validFrom, validUntil } = _.get(offers, path, null) || {};
            if (validFrom && validUntil) return `Valid from ${formatDisplayDate(validFrom)} until ${formatDisplayDate(validUntil)}`;
            if (validUntil) return `Valid until ${formatDisplayDate(validUntil)}`;
            if (validFrom) return `Valid from ${formatDisplayDate(validFrom)}`;
            return emptyStateText;
        }

        if (!path) {
            if (value) return value;
            return emptyStateText;
        }

        if (inputType === 'date') {
            const _v = _.get(offers, path, null);
            if (_v) return formatDisplayDate(_v);
            return emptyStateText;
        }

        if (inputType === 'routes') {
            if (_.get(offers, path, null)) {
                return `${_.get(offers, path).map(r => (r.source?.text || 'Everywhere') + ' to ' + (r.destination?.text || 'Everywhere')).join(' | ')}`;
            } else {
                return emptyStateText;
            }
        }

        if (inputType === 'transit-time') {
            let v = _.get(offers, path, null);
            if (v) {
                if (_.isNaN(parseFloat(v))) return v;
                const intV = parseFloat(v);
                const mins = parseInt((intV % 1) * 60);
                const days = parseInt(intV / 24);
                const hours = parseInt(intV % 24);
                return [
                    days ? (days === 1 ? `${days} day` : `${days} days`) : '',
                    hours ? (hours === 1 ? `${hours} hour` : `${hours} hours`) : '',
                    mins ? (mins === 1 ? `${mins} min` : `${mins} mins`) : '',
                ].join(' ');
            }
            return emptyStateText;
        }

        if (inputType !== 'routes') {
            if (_.get(offers, path, null)) {
                return _.get(offers, path);
            } else {
                return emptyStateText;
            }
        }
    })();

    // Check if this should be icon-only with tooltip (transit-time, date/validity, routes, carrier, display-currency)
    const isIconOnly = inputType === 'transit-time' || inputType === 'date' || inputType === 'validity-range' || inputType === 'routes' || inputType === 'carrier' || inputType === 'display-currency';
    // For a validity range, a cost rate's date fills in "until"; the dialog opens on the saved dates.
    const optionPath = inputType === 'validity-range' ? `${path}.validUntil` : path;
    const modalValue = (() => {
        if (inputType === 'validity-range') {
            const { validFrom, validUntil } = _.get(offers, path, null) || {};
            // The date boxes take YYYY-MM-DD only; a rate's date may carry a time.
            const day = d => (d && dayjs(d).isValid() ? dayjs(d).format('YYYY-MM-DD') : '');
            return { validFrom: day(validFrom), validUntil: day(validUntil) };
        }
        return path ? _.get(offers, path, '') : value;
    })();

    // A cost rate's end date can fall before the start date already set: then the start date goes,
    // rather than leave a range that ends before it begins.
    function dropStartAfterEnd(no) {
        if (inputType !== 'validity-range') return;
        const { validFrom, validUntil } = _.get(no, path, null) || {};
        if (validFrom && validUntil && dayjs(validUntil).isBefore(dayjs(validFrom), 'day')) _.unset(no, `${path}.validFrom`);
    }

    const tooltipText = isIconOnly ? (buttonText === emptyStateText ? emptyStateText : buttonText) : null;

    if (isIconOnly) {
        return (
            <Tooltip title={tooltipText || emptyStateText}>
                <span>
                    <Button
                        variant='text'
                        sx={{
                            minWidth: '32px',
                            width: '32px',
                            height: '32px',
                            padding: '0px',
                            borderRadius: '6px',
                            border: 'none',
                            color: buttonText === emptyStateText ? theme.palette.text.secondary : theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: buttonText === emptyStateText ? alpha(theme.palette.text.secondary, 0.08) : alpha(theme.palette.primary.main, 0.1),
                                color: buttonText === emptyStateText ? theme.palette.text.secondary : theme.palette.primary.main,
                                border: 'none'
                            },
                            '&.Mui-disabled': {
                                color: theme.palette.action.disabled,
                                border: 'none'
                            },
                            '&:focus': {
                                outline: 'none',
                                border: 'none'
                            }
                        }}
                        disabled={disabled}
                        onClick={() => {
                            setModal({
                                title,
                                label,
                                inputType,
                                description: _.isEmpty(options) ? null :
                                    <Box>
                                        {optionsDescription}
                                        {options.map(option => (
                                            <span
                                                key={option}
                                                style={{ textDecoration: 'underline', margin: '0px 6px', cursor: 'pointer' }}
                                                onClick={() => {
                                                    if (setOffers) {
                                                        setOffers(o => {
                                                            const no = _.cloneDeep(o);
                                                            _.set(no, optionPath, option);
                                                            dropStartAfterEnd(no);
                                                            return no;
                                                        });
                                                    }
                                                    if (setValue) {
                                                        setValue(option);
                                                    }
                                                    setModal({ ...modal, open: false });
                                                }}
                                            >
                                                {inputType === 'transit-time' ? formatMinutesSmart(option * 60) : option}
                                            </span>
                                        ))}
                                    </Box>,
                                value: modalValue,
                                setValue,
                                path,
                                open: true,
                            });
                        }}
                    >
                        {icon}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    return (
        <Button
            variant='text'
            sx={{ textTransform: 'none', minWidth: '0px', padding: emptyStateText === buttonText || forceNoPadding ? '0px' : '' }}
            color={buttonText === emptyStateText ? 'oneoffbuilderfields' : 'primary'}
            disabled={disabled}
            onClick={() => setModal({
                title,
                label,
                inputType,
                description: _.isEmpty(options) ? null :
                    <Box>
                        {optionsDescription}
                        {options.map(option => (
                            <span
                                key={option}
                                style={{ textDecoration: 'underline', margin: '0px 6px', cursor: 'pointer' }}
                                onClick={() => {
                                    if (setOffers) {
                                        setOffers(o => {
                                            const no = _.cloneDeep(o);
                                            _.set(no, optionPath, option);
                                            dropStartAfterEnd(no);
                                            return no;
                                        });
                                    }
                                    if (setValue) {
                                        setValue(option);
                                    }
                                    setModal({ ...modal, open: false });
                                }}
                            >
                                {inputType === 'transit-time' ? formatMinutesSmart(option * 60) : option}
                            </span>
                        ))}
                    </Box>,
                value: modalValue,
                setValue,
                path,
                open: true,
            })}
        >
            {icon}&nbsp;&nbsp;{buttonText}
        </Button>
    )
}

export default SpecialInputField;

function formatMinutesSmart(minutes) {
    const days = Math.floor(minutes / (60 * 24));
    const hours = Math.floor((minutes % (60 * 24)) / 60);
    const mins = minutes % 60;

    console.log(days, hours, mins);
  
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if ((hours > 0 || days > 0) && !(hours === 0 && mins === 0)) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
  
    return parts.join(':');
  }
